import React, { useState, useEffect } from 'react';
import PageHeader from '../components/Common/PageHeader';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import '../styles/Settings.css';

const SettingsView = ({ mockData, setMockData, showNotification }) => {
  const [activeTab, setActiveTab] = useState('business');
  const [settings, setSettings] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Settings categories
  const settingsTabs = [
    { id: 'business', label: 'Business', icon: '🏢', count: 12 },
    { id: 'system', label: 'System', icon: '⚙️', count: 8 },
    { id: 'communication', label: 'Communication', icon: '📧', count: 15 },
    { id: 'security', label: 'Security', icon: '🔒', count: 6 },
    { id: 'business-rules', label: 'Rules', icon: '📋', count: 9 },
    { id: 'integrations', label: 'Integrations', icon: '🔗', count: 4 }
  ];

  useEffect(() => {
    // Initialize settings from mockData or defaults
    const defaultSettings = {
      business: {
        businessHours: {
          monday: { enabled: true, open: '08:00', close: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
          tuesday: { enabled: true, open: '08:00', close: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
          wednesday: { enabled: true, open: '08:00', close: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
          thursday: { enabled: true, open: '08:00', close: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
          friday: { enabled: true, open: '08:00', close: '17:00', breaks: [{ start: '12:00', end: '13:00' }] },
          saturday: { enabled: false, open: '09:00', close: '15:00', breaks: [] },
          sunday: { enabled: false, open: '10:00', close: '14:00', breaks: [] }
        },
        holidays: [
          { name: 'Christmas Day', date: '2024-12-25', recurring: true },
          { name: 'New Year\'s Day', date: '2025-01-01', recurring: true },
          { name: 'Bank Holiday', date: '2024-05-27', recurring: false }
        ],
        appointmentPolicies: {
          cancellationWindow: 24,
          cancellationFee: 25,
          noShowFee: 50,
          advanceBookingLimit: 60,
          allowOnlineBooking: true,
          requireDeposit: false,
          depositAmount: 0,
          autoConfirm: true
        },
        defaultServiceSettings: {
          defaultDuration: 60,
          bufferBefore: 15,
          bufferAfter: 15,
          allowBackToBack: false,
          maxConcurrentBookings: 1
        }
      },
      system: {
        timezone: 'Europe/London',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h',
        currency: 'GBP',
        language: 'en-GB',
        numberFormat: 'UK',
        fiscalYearStart: 'april',
        weekStartsOn: 'monday'
      },
      communication: {
        emailTemplates: {
          appointment_confirmation: {
            subject: 'Appointment Confirmation - {{appointment_date}}',
            body: 'Dear {{customer_name}},\n\nYour appointment has been confirmed for {{appointment_date}} at {{appointment_time}} with {{practitioner_name}}.\n\nService: {{service_name}}\nDuration: {{duration}}\nLocation: {{location}}\n\nPlease arrive 10 minutes early.\n\nBest regards,\n{{practice_name}}'
          },
          appointment_reminder: {
            subject: 'Reminder: Appointment Tomorrow',
            body: 'Dear {{customer_name}},\n\nThis is a reminder that you have an appointment tomorrow at {{appointment_time}} with {{practitioner_name}}.\n\nIf you need to reschedule, please contact us as soon as possible.\n\nThank you,\n{{practice_name}}'
          },
          appointment_cancelled: {
            subject: 'Appointment Cancelled',
            body: 'Dear {{customer_name}},\n\nYour appointment scheduled for {{appointment_date}} at {{appointment_time}} has been cancelled.\n\nIf you would like to reschedule, please contact us.\n\nBest regards,\n{{practice_name}}'
          }
        },
        smsTemplates: {
          appointment_reminder: 'Hi {{customer_name}}, reminder: appointment tomorrow at {{appointment_time}} with {{practitioner_name}}. {{practice_name}}',
          appointment_confirmation: 'Hi {{customer_name}}, your appointment is confirmed for {{appointment_date}} at {{appointment_time}}. {{practice_name}}'
        },
        reminderSettings: {
          emailReminders: true,
          smsReminders: true,
          reminderTimes: [24, 2], // hours before appointment
          escalationRules: [
            { trigger: 'no_show', action: 'send_sms', delay: 15 },
            { trigger: 'late_cancellation', action: 'send_email', delay: 0 }
          ]
        }
      },
      security: {
        dataRetention: {
          customerData: 7, // years
          appointmentHistory: 5,
          financialRecords: 7,
          auditLogs: 2
        },
        gdprSettings: {
          enabled: true,
          consentRequired: true,
          rightToForgotten: true,
          dataPortability: true,
          consentRenewalPeriod: 24 // months
        },
        hipaaSettings: {
          enabled: false, // for US practices
          auditTrail: true,
          encryptionRequired: true,
          accessLogging: true
        },
        sessionSettings: {
          timeoutMinutes: 30,
          multipleSessionsAllowed: false,
          forceLogoutOnClose: false
        },
        passwordPolicy: {
          minLength: 8,
          requireUppercase: true,
          requireNumbers: true,
          requireSymbols: true,
          expiryDays: 90
        }
      },
      businessRules: {
        paymentDefaults: {
          requireDeposit: false,
          depositPercentage: 20,
          cancellationFeeType: 'percentage', // 'fixed' or 'percentage'
          cancellationFeeAmount: 50,
          refundPolicy: 'partial', // 'full', 'partial', 'none'
          refundTimeframe: 7 // days
        },
        bookingConstraints: {
          maxAdvanceBooking: 90, // days
          minAdvanceBooking: 2, // hours
          maxBookingsPerDay: 10,
          maxBookingsPerWeek: 50,
          blockWeekends: false,
          allowHolidayBooking: false
        },
        vipSettings: {
          enabled: true,
          priorityBooking: true,
          earlyAccess: 7, // days
          discountPercentage: 10,
          specialReminders: true
        },
        practitionerCoordination: {
          allowDoubleBooking: false,
          bufferBetweenPractitioners: 30, // minutes
          coordinatedBreaks: true,
          emergencyOverride: true
        }
      }
    };

    setSettings(mockData?.settings || defaultSettings);
  }, [mockData]);

  const handleSettingChange = (category, path, value) => {
    setSettings(prevSettings => {
      const newSettings = { ...prevSettings };
      const keys = path.split('.');
      let current = newSettings;
      
      // Navigate to the nested property
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setHasUnsavedChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      if (setMockData) {
        setMockData(prev => ({ ...prev, settings }));
      }
      setHasUnsavedChanges(false);
      showNotification('Settings saved successfully!', 'success');
    } catch (error) {
      showNotification('Error saving settings. Please try again.', 'error');
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      // Reset to default settings
      setSettings({}); // This would trigger useEffect to reload defaults
      setHasUnsavedChanges(false);
      showNotification('Settings reset to defaults.', 'success');
    }
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `settings_export_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showNotification('Settings exported successfully!', 'success');
  };

  const handleImportSettings = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedSettings = JSON.parse(e.target.result);
        setSettings(importedSettings);
        setHasUnsavedChanges(true);
        showNotification('Settings imported successfully!', 'success');
      } catch (error) {
        showNotification('Error importing settings. Please check the file format.', 'error');
      }
    };
    reader.readAsText(file);
  };

  // Slideout handlers
  const handleEditBusinessHours = () => {
    const formContent = (
      <BusinessHoursEditor
        businessHours={settings.business?.businessHours || {}}
        holidays={settings.business?.holidays || []}
        onSave={(businessHours, holidays) => {
          handleSettingChange('business', 'businessHours', businessHours);
          handleSettingChange('business', 'holidays', holidays);
          closeSlideout();
        }}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Business Hours & Holidays', formContent, '800px');
  };

  const handleEditEmailTemplate = (templateKey) => {
    const template = settings.communication?.emailTemplates?.[templateKey];
    const formContent = (
      <EmailTemplateEditor
        template={template}
        templateKey={templateKey}
        onSave={(updatedTemplate) => {
          handleSettingChange('communication', `emailTemplates.${templateKey}`, updatedTemplate);
          closeSlideout();
        }}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Edit Email Template', formContent, '900px');
  };

  const handleEditAppointmentPolicies = () => {
    const formContent = (
      <AppointmentPoliciesEditor
        policies={settings.business?.appointmentPolicies || {}}
        onSave={(policies) => {
          handleSettingChange('business', 'appointmentPolicies', policies);
          closeSlideout();
        }}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Appointment Policies', formContent, '700px');
  };

  const handleEditSecuritySettings = () => {
    const formContent = (
      <SecuritySettingsEditor
        securitySettings={settings.security || {}}
        onSave={(securitySettings) => {
          handleSettingChange('security', '', securitySettings);
          closeSlideout();
        }}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Security & Compliance Settings', formContent, '800px');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'business':
        return (
          <BusinessOperationsSettings
            settings={settings.business || {}}
            onEditBusinessHours={handleEditBusinessHours}
            onEditAppointmentPolicies={handleEditAppointmentPolicies}
            onSettingChange={(path, value) => handleSettingChange('business', path, value)}
          />
        );
      case 'system':
        return (
          <SystemConfigurationSettings
            settings={settings.system || {}}
            onSettingChange={(path, value) => handleSettingChange('system', path, value)}
          />
        );
      case 'communication':
        return (
          <CommunicationSettings
            settings={settings.communication || {}}
            onEditEmailTemplate={handleEditEmailTemplate}
            onSettingChange={(path, value) => handleSettingChange('communication', path, value)}
          />
        );
      case 'security':
        return (
          <SecurityComplianceSettings
            settings={settings.security || {}}
            onEditSecuritySettings={handleEditSecuritySettings}
            onSettingChange={(path, value) => handleSettingChange('security', path, value)}
          />
        );
      case 'business-rules':
        return (
          <BusinessRulesSettings
            settings={settings.businessRules || {}}
            onSettingChange={(path, value) => handleSettingChange('businessRules', path, value)}
          />
        );
      case 'integrations':
        return (
          <IntegrationsSettings
            settings={settings.integrations || {}}
            onSettingChange={(path, value) => handleSettingChange('integrations', path, value)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings-view">
      {/* Header */}
      <PageHeader pageKey="settings">
        <input
          type="file"
          accept=".json"
          onChange={handleImportSettings}
          style={{ display: 'none' }}
          id="import-settings"
        />
        <label htmlFor="import-settings" className="btn btn-secondary">
          📥 Import
        </label>
        <button onClick={handleExportSettings} className="btn btn-secondary">
          📤 Export
        </button>
        <button onClick={handleResetSettings} className="btn btn-secondary">
          🔄 Reset
        </button>
        <button
          onClick={handleSaveSettings}
          className={`btn btn-primary ${hasUnsavedChanges ? 'pulse' : ''}`}
          disabled={!hasUnsavedChanges}
        >
          💾 Save Changes
        </button>
      </PageHeader>

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="unsaved-warning">
          <div className="warning-content">
            <span className="warning-icon">⚠️</span>
            <span>You have unsaved changes</span>
            <button onClick={handleSaveSettings} className="btn btn-sm btn-primary">
              Save Now
            </button>
          </div>
        </div>
      )}

      {/* Settings Tabs */}
      <div className="settings-tabs">
        {settingsTabs.map(tab => (
          <button
            key={tab.id}
            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            <span className="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="settings-content">
        {renderTabContent()}
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Business Operations Settings Component
const BusinessOperationsSettings = ({ settings, onEditBusinessHours, onEditAppointmentPolicies, onSettingChange }) => {
  const formatBusinessHours = (businessHours) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const openDays = days.filter(day => businessHours[day]?.enabled).length;
    return `${openDays} days open`;
  };

  return (
    <div className="settings-section">
      <div className="settings-cards">
        {/* Business Hours Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">🕐</div>
            <div>
              <h3>Business Hours</h3>
              <p>Set operating hours and holiday schedules</p>
            </div>
            <button onClick={onEditBusinessHours} className="btn btn-secondary btn-sm">
              Edit
            </button>
          </div>
          <div className="card-content">
            <div className="setting-summary">
              <span className="summary-label">Current Schedule:</span>
              <span className="summary-value">{formatBusinessHours(settings.businessHours || {})}</span>
            </div>
            <div className="setting-summary">
              <span className="summary-label">Holidays Configured:</span>
              <span className="summary-value">{settings.holidays?.length || 0}</span>
            </div>
          </div>
        </div>

        {/* Appointment Policies Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">📋</div>
            <div>
              <h3>Appointment Policies</h3>
              <p>Cancellation rules, fees, and booking limits</p>
            </div>
            <button onClick={onEditAppointmentPolicies} className="btn btn-secondary btn-sm">
              Edit
            </button>
          </div>
          <div className="card-content">
            <div className="setting-summary">
              <span className="summary-label">Cancellation Window:</span>
              <span className="summary-value">{settings.appointmentPolicies?.cancellationWindow || 24} hours</span>
            </div>
            <div className="setting-summary">
              <span className="summary-label">Advance Booking:</span>
              <span className="summary-value">{settings.appointmentPolicies?.advanceBookingLimit || 60} days</span>
            </div>
          </div>
        </div>

        {/* Default Service Settings Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">⚕️</div>
            <div>
              <h3>Default Service Settings</h3>
              <p>Duration, buffer times, and pricing rules</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Default Duration (minutes)</label>
                  <input
                    type="number"
                    value={settings.defaultServiceSettings?.defaultDuration || 60}
                    onChange={(e) => onSettingChange('defaultServiceSettings.defaultDuration', parseInt(e.target.value))}
                    min="15"
                    max="480"
                    step="15"
                  />
                </div>
                <div className="form-group">
                  <label>Buffer Before (minutes)</label>
                  <input
                    type="number"
                    value={settings.defaultServiceSettings?.bufferBefore || 15}
                    onChange={(e) => onSettingChange('defaultServiceSettings.bufferBefore', parseInt(e.target.value))}
                    min="0"
                    max="60"
                    step="5"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Buffer After (minutes)</label>
                  <input
                    type="number"
                    value={settings.defaultServiceSettings?.bufferAfter || 15}
                    onChange={(e) => onSettingChange('defaultServiceSettings.bufferAfter', parseInt(e.target.value))}
                    min="0"
                    max="60"
                    step="5"
                  />
                </div>
                <div className="form-group">
                  <label>Max Concurrent Bookings</label>
                  <input
                    type="number"
                    value={settings.defaultServiceSettings?.maxConcurrentBookings || 1}
                    onChange={(e) => onSettingChange('defaultServiceSettings.maxConcurrentBookings', parseInt(e.target.value))}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.defaultServiceSettings?.allowBackToBack || false}
                    onChange={(e) => onSettingChange('defaultServiceSettings.allowBackToBack', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Allow back-to-back appointments
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Multi-location Policies Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">🏥</div>
            <div>
              <h3>Multi-location Policies</h3>
              <p>Operational policies and location overrides</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.multiLocationPolicies?.enableCrossBooking || false}
                    onChange={(e) => onSettingChange('multiLocationPolicies.enableCrossBooking', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Allow cross-location booking
                </label>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.multiLocationPolicies?.syncPractitionerSchedules || false}
                    onChange={(e) => onSettingChange('multiLocationPolicies.syncPractitionerSchedules', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Synchronize practitioner schedules across locations
                </label>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.multiLocationPolicies?.inheritGlobalSettings || true}
                    onChange={(e) => onSettingChange('multiLocationPolicies.inheritGlobalSettings', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Inherit global settings for new locations
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Configuration Settings Component
const SystemConfigurationSettings = ({ settings, onSettingChange }) => {
  const timezones = [
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'America/New_York', label: 'New York (EST)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
    { value: 'America/Chicago', label: 'Chicago (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
  ];

  const currencies = [
    { value: 'GBP', label: '£ British Pound', symbol: '£' },
    { value: 'USD', label: '$ US Dollar', symbol: '$' },
    { value: 'EUR', label: '€ Euro', symbol: '€' },
    { value: 'CAD', label: 'C$ Canadian Dollar', symbol: 'C$' },
    { value: 'AUD', label: 'A$ Australian Dollar', symbol: 'A$' }
  ];

  const languages = [
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'fr-FR', label: 'Français (France)' },
    { value: 'de-DE', label: 'Deutsch (Deutschland)' },
    { value: 'es-ES', label: 'Español (España)' },
    { value: 'it-IT', label: 'Italiano (Italia)' }
  ];

  return (
    <div className="settings-section">
      <div className="settings-cards">
        {/* Regional Settings Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">🌍</div>
            <div>
              <h3>Regional Settings</h3>
              <p>Time zone, currency, and localization preferences</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Time Zone</label>
                  <select
                    value={settings.timezone || 'Europe/London'}
                    onChange={(e) => onSettingChange('timezone', e.target.value)}
                  >
                    {timezones.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select
                    value={settings.currency || 'GBP'}
                    onChange={(e) => onSettingChange('currency', e.target.value)}
                  >
                    {currencies.map(curr => (
                      <option key={curr.value} value={curr.value}>{curr.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Language</label>
                  <select
                    value={settings.language || 'en-GB'}
                    onChange={(e) => onSettingChange('language', e.target.value)}
                  >
                    {languages.map(lang => (
                      <option key={lang.value} value={lang.value}>{lang.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Number Format</label>
                  <select
                    value={settings.numberFormat || 'UK'}
                    onChange={(e) => onSettingChange('numberFormat', e.target.value)}
                  >
                    <option value="UK">UK (1,234.56)</option>
                    <option value="US">US (1,234.56)</option>
                    <option value="EU">EU (1.234,56)</option>
                    <option value="IN">India (1,23,456.78)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Date & Time Formats Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">📅</div>
            <div>
              <h3>Date & Time Formats</h3>
              <p>Configure date and time display preferences</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Date Format</label>
                  <select
                    value={settings.dateFormat || 'DD/MM/YYYY'}
                    onChange={(e) => onSettingChange('dateFormat', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                    <option value="DD-MMM-YYYY">DD-MMM-YYYY (31-Dec-2024)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Time Format</label>
                  <select
                    value={settings.timeFormat || '24h'}
                    onChange={(e) => onSettingChange('timeFormat', e.target.value)}
                  >
                    <option value="24h">24 Hour (14:30)</option>
                    <option value="12h">12 Hour (2:30 PM)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Week Starts On</label>
                  <select
                    value={settings.weekStartsOn || 'monday'}
                    onChange={(e) => onSettingChange('weekStartsOn', e.target.value)}
                  >
                    <option value="monday">Monday</option>
                    <option value="sunday">Sunday</option>
                    <option value="saturday">Saturday</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fiscal Year Starts</label>
                  <select
                    value={settings.fiscalYearStart || 'april'}
                    onChange={(e) => onSettingChange('fiscalYearStart', e.target.value)}
                  >
                    <option value="january">January</option>
                    <option value="april">April</option>
                    <option value="july">July</option>
                    <option value="october">October</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tax Configuration Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">🧾</div>
            <div>
              <h3>Tax Configuration</h3>
              <p>Tax rates and invoicing templates</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Default Tax Rate (%)</label>
                  <input
                    type="number"
                    value={settings.defaultTaxRate || 20}
                    onChange={(e) => onSettingChange('defaultTaxRate', parseFloat(e.target.value))}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Tax ID Number</label>
                  <input
                    type="text"
                    value={settings.taxIdNumber || ''}
                    onChange={(e) => onSettingChange('taxIdNumber', e.target.value)}
                    placeholder="Enter tax ID number"
                  />
                </div>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.taxInclusive || false}
                    onChange={(e) => onSettingChange('taxInclusive', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Prices include tax
                </label>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.showTaxBreakdown || true}
                    onChange={(e) => onSettingChange('showTaxBreakdown', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Show tax breakdown on invoices
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Communication Settings Component
const CommunicationSettings = ({ settings, onEditEmailTemplate, onSettingChange }) => {
  const emailTemplates = settings.emailTemplates || {};
  const smsTemplates = settings.smsTemplates || {};
  const reminderSettings = settings.reminderSettings || {};

  const templateTypes = [
    { key: 'appointment_confirmation', name: 'Appointment Confirmation', icon: <span className="icon icon--check icon--success"></span> },
    { key: 'appointment_reminder', name: 'Appointment Reminder', icon: <span className="icon icon--clock icon--default"></span> },
    { key: 'appointment_cancelled', name: 'Appointment Cancelled', icon: <span className="icon icon--close icon--delete"></span> },
    { key: 'payment_reminder', name: 'Payment Reminder', icon: <span className="icon icon--money icon--default"></span> },
    { key: 'welcome_email', name: 'Welcome Email', icon: <span className="icon icon--wave icon--default"></span> }
  ];

  return (
    <div className="settings-section">
      <div className="settings-cards">
        {/* Email Templates Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">📧</div>
            <div>
              <h3>Email Templates</h3>
              <p>Customize automated email messages</p>
            </div>
          </div>
          <div className="card-content">
            <div className="template-list">
              {templateTypes.map(template => (
                <div key={template.key} className="template-item">
                  <div className="template-info">
                    <span className="template-icon">{template.icon}</span>
                    <div>
                      <h4>{template.name}</h4>
                      <p>Subject: {emailTemplates[template.key]?.subject || 'Not configured'}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => onEditEmailTemplate(template.key)}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SMS Templates Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">📱</div>
            <div>
              <h3>SMS Templates</h3>
              <p>Configure SMS message templates</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-group">
                <label>Appointment Reminder SMS</label>
                <textarea
                  value={smsTemplates.appointment_reminder || ''}
                  onChange={(e) => onSettingChange('smsTemplates.appointment_reminder', e.target.value)}
                  placeholder="Hi {{customer_name}}, reminder: appointment tomorrow at {{appointment_time}}..."
                  rows="3"
                  maxLength="160"
                />
                <small className="char-count">{(smsTemplates.appointment_reminder || '').length}/160 characters</small>
              </div>
              <div className="form-group">
                <label>Appointment Confirmation SMS</label>
                <textarea
                  value={smsTemplates.appointment_confirmation || ''}
                  onChange={(e) => onSettingChange('smsTemplates.appointment_confirmation', e.target.value)}
                  placeholder="Hi {{customer_name}}, your appointment is confirmed..."
                  rows="3"
                  maxLength="160"
                />
                <small className="char-count">{(smsTemplates.appointment_confirmation || '').length}/160 characters</small>
              </div>
            </div>
          </div>
        </div>

        {/* Reminder Settings Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">⏰</div>
            <div>
              <h3>Reminder Settings</h3>
              <p>Configure automated reminder timing and escalation</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={reminderSettings.emailReminders || false}
                    onChange={(e) => onSettingChange('reminderSettings.emailReminders', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Send email reminders
                </label>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={reminderSettings.smsReminders || false}
                    onChange={(e) => onSettingChange('reminderSettings.smsReminders', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Send SMS reminders
                </label>
              </div>
              <div className="form-group">
                <label>Reminder Times (hours before appointment)</label>
                <div className="reminder-times">
                  {(reminderSettings.reminderTimes || [24, 2]).map((time, index) => (
                    <div key={index} className="reminder-time-item">
                      <input
                        type="number"
                        value={time}
                        onChange={(e) => {
                          const newTimes = [...(reminderSettings.reminderTimes || [24, 2])];
                          newTimes[index] = parseInt(e.target.value);
                          onSettingChange('reminderSettings.reminderTimes', newTimes);
                        }}
                        min="0"
                        max="168"
                      />
                      <span>hours</span>
                      {reminderSettings.reminderTimes && reminderSettings.reminderTimes.length > 1 && (
                        <button
                          onClick={() => {
                            const newTimes = reminderSettings.reminderTimes.filter((_, i) => i !== index);
                            onSettingChange('reminderSettings.reminderTimes', newTimes);
                          }}
                          className="btn btn-sm btn-danger"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newTimes = [...(reminderSettings.reminderTimes || [24, 2]), 1];
                      onSettingChange('reminderSettings.reminderTimes', newTimes);
                    }}
                    className="btn btn-sm btn-secondary"
                  >
                    + Add Reminder
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Communication Preferences Card */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-icon">🔔</div>
            <div>
              <h3>Notification Triggers</h3>
              <p>Configure custom notification triggers and escalation rules</p>
            </div>
          </div>
          <div className="card-content">
            <div className="settings-form">
              <div className="escalation-rules">
                {(reminderSettings.escalationRules || []).map((rule, index) => (
                  <div key={index} className="escalation-rule">
                    <div className="rule-row">
                      <select
                        value={rule.trigger}
                        onChange={(e) => {
                          const newRules = [...(reminderSettings.escalationRules || [])];
                          newRules[index].trigger = e.target.value;
                          onSettingChange('reminderSettings.escalationRules', newRules);
                        }}
                      >
                        <option value="no_show">No Show</option>
                        <option value="late_cancellation">Late Cancellation</option>
                        <option value="payment_overdue">Payment Overdue</option>
                        <option value="appointment_approaching">Appointment Approaching</option>
                      </select>
                      <select
                        value={rule.action}
                        onChange={(e) => {
                          const newRules = [...(reminderSettings.escalationRules || [])];
                          newRules[index].action = e.target.value;
                          onSettingChange('reminderSettings.escalationRules', newRules);
                        }}
                      >
                        <option value="send_email">Send Email</option>
                        <option value="send_sms">Send SMS</option>
                        <option value="create_task">Create Task</option>
                        <option value="notify_manager">Notify Manager</option>
                      </select>
                      <input
                        type="number"
                        value={rule.delay}
                        onChange={(e) => {
                          const newRules = [...(reminderSettings.escalationRules || [])];
                          newRules[index].delay = parseInt(e.target.value);
                          onSettingChange('reminderSettings.escalationRules', newRules);
                        }}
                        placeholder="Delay (minutes)"
                        min="0"
                      />
                      <button
                        onClick={() => {
                          const newRules = reminderSettings.escalationRules.filter((_, i) => i !== index);
                          onSettingChange('reminderSettings.escalationRules', newRules);
                        }}
                        className="btn btn-sm btn-danger"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newRules = [
                      ...(reminderSettings.escalationRules || []),
                      { trigger: 'no_show', action: 'send_sms', delay: 15 }
                    ];
                    onSettingChange('reminderSettings.escalationRules', newRules);
                  }}
                  className="btn btn-sm btn-secondary"
                >
                  + Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Security & Compliance Settings Component
const SecurityComplianceSettings = ({ settings, onSettingChange, onSlideoutOpen }) => {
  return (
    <div className="settings-category">
      <div className="settings-section">
        <h3>Data Retention Policies</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Patient Data Retention</h4>
            <div className="setting-row">
              <label>Retention Period (years)</label>
              <select 
                value={settings.security?.dataRetention?.patientRecords || 7}
                onChange={(e) => onSettingChange('security', 'dataRetention.patientRecords', parseInt(e.target.value))}
              >
                <option value="3">3 years</option>
                <option value="5">5 years</option>
                <option value="7">7 years</option>
                <option value="10">10 years</option>
                <option value="15">15 years</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Automated Archival</label>
              <input
                type="checkbox"
                checked={settings.security?.dataRetention?.autoArchive || false}
                onChange={(e) => onSettingChange('security', 'dataRetention.autoArchive', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Archive Notification Lead Time (days)</label>
              <input
                type="number"
                value={settings.security?.dataRetention?.notificationLeadTime || 30}
                onChange={(e) => onSettingChange('security', 'dataRetention.notificationLeadTime', parseInt(e.target.value))}
                min="7"
                max="90"
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Communication Data</h4>
            <div className="setting-row">
              <label>Email Retention (months)</label>
              <select 
                value={settings.security?.dataRetention?.emailRetention || 24}
                onChange={(e) => onSettingChange('security', 'dataRetention.emailRetention', parseInt(e.target.value))}
              >
                <option value="6">6 months</option>
                <option value="12">12 months</option>
                <option value="24">24 months</option>
                <option value="36">36 months</option>
              </select>
            </div>
            <div className="setting-row">
              <label>SMS/Call Log Retention (months)</label>
              <select 
                value={settings.security?.dataRetention?.smsRetention || 12}
                onChange={(e) => onSettingChange('security', 'dataRetention.smsRetention', parseInt(e.target.value))}
              >
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months</option>
                <option value="24">24 months</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Privacy & Compliance</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>GDPR Compliance</h4>
            <div className="setting-row">
              <label>Enable GDPR Features</label>
              <input
                type="checkbox"
                checked={settings.security?.gdpr?.enabled || false}
                onChange={(e) => onSettingChange('security', 'gdpr.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Cookie Consent Required</label>
              <input
                type="checkbox"
                checked={settings.security?.gdpr?.cookieConsent || false}
                onChange={(e) => onSettingChange('security', 'gdpr.cookieConsent', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Data Subject Rights Portal</label>
              <input
                type="checkbox"
                checked={settings.security?.gdpr?.dataSubjectPortal || false}
                onChange={(e) => onSettingChange('security', 'gdpr.dataSubjectPortal', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Automated Data Export</label>
              <input
                type="checkbox"
                checked={settings.security?.gdpr?.autoExport || false}
                onChange={(e) => onSettingChange('security', 'gdpr.autoExport', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>HIPAA Compliance</h4>
            <div className="setting-row">
              <label>HIPAA Mode Enabled</label>
              <input
                type="checkbox"
                checked={settings.security?.hipaa?.enabled || false}
                onChange={(e) => onSettingChange('security', 'hipaa.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Business Associate Agreements</label>
              <input
                type="checkbox"
                checked={settings.security?.hipaa?.baaRequired || false}
                onChange={(e) => onSettingChange('security', 'hipaa.baaRequired', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Minimum Necessary Standard</label>
              <input
                type="checkbox"
                checked={settings.security?.hipaa?.minimumNecessary || false}
                onChange={(e) => onSettingChange('security', 'hipaa.minimumNecessary', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Audit Trail Level</label>
              <select 
                value={settings.security?.hipaa?.auditLevel || 'comprehensive'}
                onChange={(e) => onSettingChange('security', 'hipaa.auditLevel', e.target.value)}
              >
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="comprehensive">Comprehensive</option>
                <option value="forensic">Forensic</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Session & Access Management</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Session Settings</h4>
            <div className="setting-row">
              <label>Session Timeout (minutes)</label>
              <select 
                value={settings.security?.session?.timeout || 30}
                onChange={(e) => onSettingChange('security', 'session.timeout', parseInt(e.target.value))}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="240">4 hours</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Concurrent Sessions Allowed</label>
              <input
                type="number"
                value={settings.security?.session?.maxConcurrent || 3}
                onChange={(e) => onSettingChange('security', 'session.maxConcurrent', parseInt(e.target.value))}
                min="1"
                max="10"
              />
            </div>
            <div className="setting-row">
              <label>Remember Login</label>
              <input
                type="checkbox"
                checked={settings.security?.session?.rememberLogin || false}
                onChange={(e) => onSettingChange('security', 'session.rememberLogin', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>IP Address Validation</label>
              <input
                type="checkbox"
                checked={settings.security?.session?.ipValidation || false}
                onChange={(e) => onSettingChange('security', 'session.ipValidation', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Password Policies</h4>
            <div className="setting-row">
              <label>Minimum Password Length</label>
              <select 
                value={settings.security?.password?.minLength || 8}
                onChange={(e) => onSettingChange('security', 'password.minLength', parseInt(e.target.value))}
              >
                <option value="6">6 characters</option>
                <option value="8">8 characters</option>
                <option value="10">10 characters</option>
                <option value="12">12 characters</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Require Special Characters</label>
              <input
                type="checkbox"
                checked={settings.security?.password?.requireSpecial || false}
                onChange={(e) => onSettingChange('security', 'password.requireSpecial', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Require Numbers</label>
              <input
                type="checkbox"
                checked={settings.security?.password?.requireNumbers || false}
                onChange={(e) => onSettingChange('security', 'password.requireNumbers', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Password Expiration (days)</label>
              <select 
                value={settings.security?.password?.expiration || 90}
                onChange={(e) => onSettingChange('security', 'password.expiration', parseInt(e.target.value))}
              >
                <option value="0">Never</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Security Monitoring</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Alert Configuration</h4>
            <div className="setting-row">
              <label>Failed Login Alert Threshold</label>
              <select 
                value={settings.security?.monitoring?.failedLoginThreshold || 5}
                onChange={(e) => onSettingChange('security', 'monitoring.failedLoginThreshold', parseInt(e.target.value))}
              >
                <option value="3">3 attempts</option>
                <option value="5">5 attempts</option>
                <option value="10">10 attempts</option>
                <option value="15">15 attempts</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Suspicious Activity Monitoring</label>
              <input
                type="checkbox"
                checked={settings.security?.monitoring?.suspiciousActivity || false}
                onChange={(e) => onSettingChange('security', 'monitoring.suspiciousActivity', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Data Access Logging</label>
              <input
                type="checkbox"
                checked={settings.security?.monitoring?.dataAccessLog || false}
                onChange={(e) => onSettingChange('security', 'monitoring.dataAccessLog', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Real-time Security Alerts</label>
              <input
                type="checkbox"
                checked={settings.security?.monitoring?.realTimeAlerts || false}
                onChange={(e) => onSettingChange('security', 'monitoring.realTimeAlerts', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Backup & Recovery</h4>
            <div className="setting-row">
              <label>Automated Backups</label>
              <input
                type="checkbox"
                checked={settings.security?.backup?.enabled || false}
                onChange={(e) => onSettingChange('security', 'backup.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Backup Frequency</label>
              <select 
                value={settings.security?.backup?.frequency || 'daily'}
                onChange={(e) => onSettingChange('security', 'backup.frequency', e.target.value)}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Backup Retention (days)</label>
              <select 
                value={settings.security?.backup?.retention || 30}
                onChange={(e) => onSettingChange('security', 'backup.retention', parseInt(e.target.value))}
              >
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="365">1 year</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Offsite Backup</label>
              <input
                type="checkbox"
                checked={settings.security?.backup?.offsite || false}
                onChange={(e) => onSettingChange('security', 'backup.offsite', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="btn btn-secondary"
          onClick={() => onSlideoutOpen('securitySettings')}
        >
          Advanced Security Settings
        </button>
      </div>
    </div>
  );
};

// Business Rules Settings Component
const BusinessRulesSettings = ({ settings, onSettingChange, onSlideoutOpen }) => {
  return (
    <div className="settings-category">
      <div className="settings-section">
        <h3>Payment & Billing Rules</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Payment Defaults</h4>
            <div className="setting-row">
              <label>Default Payment Method</label>
              <select 
                value={settings.businessRules?.payment?.defaultMethod || 'card'}
                onChange={(e) => onSettingChange('businessRules', 'payment.defaultMethod', e.target.value)}
              >
                <option value="card">Credit/Debit Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="insurance">Insurance</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Payment Due Period (days)</label>
              <select 
                value={settings.businessRules?.payment?.duePeriod || 30}
                onChange={(e) => onSettingChange('businessRules', 'payment.duePeriod', parseInt(e.target.value))}
              >
                <option value="0">Immediate</option>
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Late Payment Fee (%)</label>
              <input
                type="number"
                value={settings.businessRules?.payment?.lateFeePercentage || 5}
                onChange={(e) => onSettingChange('businessRules', 'payment.lateFeePercentage', parseFloat(e.target.value))}
                min="0"
                max="25"
                step="0.5"
              />
            </div>
            <div className="setting-row">
              <label>Auto-charge for No-shows</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.payment?.autoChargeNoShow || false}
                onChange={(e) => onSettingChange('businessRules', 'payment.autoChargeNoShow', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Billing Configuration</h4>
            <div className="setting-row">
              <label>Invoice Template</label>
              <select 
                value={settings.businessRules?.billing?.invoiceTemplate || 'standard'}
                onChange={(e) => onSettingChange('businessRules', 'billing.invoiceTemplate', e.target.value)}
              >
                <option value="standard">Standard</option>
                <option value="detailed">Detailed</option>
                <option value="minimal">Minimal</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Auto-send Invoices</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.billing?.autoSendInvoices || false}
                onChange={(e) => onSettingChange('businessRules', 'billing.autoSendInvoices', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Invoice Numbering Format</label>
              <select 
                value={settings.businessRules?.billing?.numberingFormat || 'sequential'}
                onChange={(e) => onSettingChange('businessRules', 'billing.numberingFormat', e.target.value)}
              >
                <option value="sequential">Sequential (001, 002, ...)</option>
                <option value="date_based">Date-based (2024-001)</option>
                <option value="location_based">Location-based (LOC1-001)</option>
                <option value="custom">Custom Format</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Tax Calculation Method</label>
              <select 
                value={settings.businessRules?.billing?.taxCalculation || 'inclusive'}
                onChange={(e) => onSettingChange('businessRules', 'billing.taxCalculation', e.target.value)}
              >
                <option value="inclusive">Tax Inclusive</option>
                <option value="exclusive">Tax Exclusive</option>
                <option value="exempt">Tax Exempt</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Booking Constraints & Policies</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Booking Limits</h4>
            <div className="setting-row">
              <label>Max Appointments per Day</label>
              <input
                type="number"
                value={settings.businessRules?.booking?.maxPerDay || 20}
                onChange={(e) => onSettingChange('businessRules', 'booking.maxPerDay', parseInt(e.target.value))}
                min="1"
                max="50"
              />
            </div>
            <div className="setting-row">
              <label>Max Appointments per Customer per Day</label>
              <input
                type="number"
                value={settings.businessRules?.booking?.maxPerCustomerPerDay || 3}
                onChange={(e) => onSettingChange('businessRules', 'booking.maxPerCustomerPerDay', parseInt(e.target.value))}
                min="1"
                max="10"
              />
            </div>
            <div className="setting-row">
              <label>Minimum Time Between Appointments (minutes)</label>
              <select 
                value={settings.businessRules?.booking?.minTimeBetween || 15}
                onChange={(e) => onSettingChange('businessRules', 'booking.minTimeBetween', parseInt(e.target.value))}
              >
                <option value="0">No gap</option>
                <option value="5">5 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Allow Double Booking</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.booking?.allowDoubleBooking || false}
                onChange={(e) => onSettingChange('businessRules', 'booking.allowDoubleBooking', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Advanced Booking Rules</h4>
            <div className="setting-row">
              <label>Require Deposit for New Customers</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.booking?.requireDepositNewCustomer || false}
                onChange={(e) => onSettingChange('businessRules', 'booking.requireDepositNewCustomer', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Deposit Amount (%)</label>
              <input
                type="number"
                value={settings.businessRules?.booking?.depositPercentage || 25}
                onChange={(e) => onSettingChange('businessRules', 'booking.depositPercentage', parseInt(e.target.value))}
                min="10"
                max="100"
                step="5"
              />
            </div>
            <div className="setting-row">
              <label>Auto-confirm Online Bookings</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.booking?.autoConfirmOnline || true}
                onChange={(e) => onSettingChange('businessRules', 'booking.autoConfirmOnline', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Block Weekend Bookings</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.booking?.blockWeekends || false}
                onChange={(e) => onSettingChange('businessRules', 'booking.blockWeekends', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>VIP & Priority Settings</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>VIP Customer Rules</h4>
            <div className="setting-row">
              <label>VIP Identification Criteria</label>
              <select 
                value={settings.businessRules?.vip?.criteria || 'manual'}
                onChange={(e) => onSettingChange('businessRules', 'vip.criteria', e.target.value)}
              >
                <option value="manual">Manual Assignment</option>
                <option value="spend_threshold">Spending Threshold</option>
                <option value="visit_frequency">Visit Frequency</option>
                <option value="membership">Membership Level</option>
              </select>
            </div>
            <div className="setting-row">
              <label>VIP Spending Threshold (£)</label>
              <input
                type="number"
                value={settings.businessRules?.vip?.spendingThreshold || 1000}
                onChange={(e) => onSettingChange('businessRules', 'vip.spendingThreshold', parseInt(e.target.value))}
                min="100"
                step="100"
              />
            </div>
            <div className="setting-row">
              <label>VIP Booking Priority</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.vip?.bookingPriority || false}
                onChange={(e) => onSettingChange('businessRules', 'vip.bookingPriority', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>VIP Discount (%)</label>
              <input
                type="number"
                value={settings.businessRules?.vip?.discount || 10}
                onChange={(e) => onSettingChange('businessRules', 'vip.discount', parseInt(e.target.value))}
                min="0"
                max="50"
                step="5"
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Priority Handling</h4>
            <div className="setting-row">
              <label>Emergency Appointment Slots</label>
              <input
                type="number"
                value={settings.businessRules?.priority?.emergencySlots || 2}
                onChange={(e) => onSettingChange('businessRules', 'priority.emergencySlots', parseInt(e.target.value))}
                min="0"
                max="10"
              />
            </div>
            <div className="setting-row">
              <label>Same-day Booking Cutoff (hours)</label>
              <input
                type="number"
                value={settings.businessRules?.priority?.sameDayCutoff || 2}
                onChange={(e) => onSettingChange('businessRules', 'priority.sameDayCutoff', parseInt(e.target.value))}
                min="1"
                max="12"
              />
            </div>
            <div className="setting-row">
              <label>Urgent Appointment Surcharge (%)</label>
              <input
                type="number"
                value={settings.businessRules?.priority?.urgentSurcharge || 25}
                onChange={(e) => onSettingChange('businessRules', 'priority.urgentSurcharge', parseInt(e.target.value))}
                min="0"
                max="100"
                step="5"
              />
            </div>
            <div className="setting-row">
              <label>Allow Overbooking for Emergencies</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.priority?.allowOverbooking || false}
                onChange={(e) => onSettingChange('businessRules', 'priority.allowOverbooking', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Practitioner Coordination</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Scheduling Rules</h4>
            <div className="setting-row">
              <label>Allow Cross-location Assignments</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.practitioner?.crossLocation || false}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.crossLocation', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Auto-assign Based on Availability</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.practitioner?.autoAssign || true}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.autoAssign', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Skill-based Assignment</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.practitioner?.skillBased || false}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.skillBased', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Load Balancing Method</label>
              <select 
                value={settings.businessRules?.practitioner?.loadBalancing || 'round_robin'}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.loadBalancing', e.target.value)}
              >
                <option value="round_robin">Round Robin</option>
                <option value="least_busy">Least Busy</option>
                <option value="random">Random</option>
                <option value="customer_preference">Customer Preference</option>
              </select>
            </div>
          </div>

          <div className="setting-card">
            <h4>Availability Management</h4>
            <div className="setting-row">
              <label>Break Time Buffer (minutes)</label>
              <input
                type="number"
                value={settings.businessRules?.practitioner?.breakBuffer || 15}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.breakBuffer', parseInt(e.target.value))}
                min="0"
                max="60"
                step="5"
              />
            </div>
            <div className="setting-row">
              <label>Maximum Daily Hours</label>
              <input
                type="number"
                value={settings.businessRules?.practitioner?.maxDailyHours || 8}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.maxDailyHours', parseInt(e.target.value))}
                min="4"
                max="12"
                step="0.5"
              />
            </div>
            <div className="setting-row">
              <label>Require Break After (hours)</label>
              <input
                type="number"
                value={settings.businessRules?.practitioner?.breakAfterHours || 4}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.breakAfterHours', parseInt(e.target.value))}
                min="2"
                max="8"
                step="0.5"
              />
            </div>
            <div className="setting-row">
              <label>Allow Overtime</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.practitioner?.allowOvertime || false}
                onChange={(e) => onSettingChange('businessRules', 'practitioner.allowOvertime', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Automated Rules & Workflows</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Workflow Triggers</h4>
            <div className="setting-row">
              <label>Auto-reschedule on Cancellation</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.automation?.autoReschedule || false}
                onChange={(e) => onSettingChange('businessRules', 'automation.autoReschedule', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Auto-fill Cancellation Slots</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.automation?.autoFillSlots || false}
                onChange={(e) => onSettingChange('businessRules', 'automation.autoFillSlots', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Send Follow-up Tasks</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.automation?.followUpTasks || false}
                onChange={(e) => onSettingChange('businessRules', 'automation.followUpTasks', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Auto-generate Treatment Plans</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.automation?.autoTreatmentPlans || false}
                onChange={(e) => onSettingChange('businessRules', 'automation.autoTreatmentPlans', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Compliance Automation</h4>
            <div className="setting-row">
              <label>Auto-archive Old Records</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.compliance?.autoArchive || false}
                onChange={(e) => onSettingChange('businessRules', 'compliance.autoArchive', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Consent Renewal Alerts</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.compliance?.consentAlerts || false}
                onChange={(e) => onSettingChange('businessRules', 'compliance.consentAlerts', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Regulatory Reporting</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.compliance?.autoReporting || false}
                onChange={(e) => onSettingChange('businessRules', 'compliance.autoReporting', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Data Retention Enforcement</label>
              <input
                type="checkbox"
                checked={settings.businessRules?.compliance?.dataRetentionEnforcement || false}
                onChange={(e) => onSettingChange('businessRules', 'compliance.dataRetentionEnforcement', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="btn btn-secondary"
          onClick={() => onSlideoutOpen('businessRulesEditor')}
        >
          Advanced Business Rules Editor
        </button>
      </div>
    </div>
  );
};

// Integrations Settings Component
const IntegrationsSettings = ({ settings, onSettingChange, onSlideoutOpen }) => {
  return (
    <div className="settings-category">
      <div className="settings-section">
        <h3>Third-party Integrations</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Payment Processors</h4>
            <div className="setting-row">
              <label>Stripe Integration</label>
              <input
                type="checkbox"
                checked={settings.integrations?.stripe?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'stripe.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>PayPal Integration</label>
              <input
                type="checkbox"
                checked={settings.integrations?.paypal?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'paypal.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Square Integration</label>
              <input
                type="checkbox"
                checked={settings.integrations?.square?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'square.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Default Payment Gateway</label>
              <select 
                value={settings.integrations?.defaultPaymentGateway || 'stripe'}
                onChange={(e) => onSettingChange('integrations', 'defaultPaymentGateway', e.target.value)}
              >
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="square">Square</option>
                <option value="manual">Manual Processing</option>
              </select>
            </div>
          </div>

          <div className="setting-card">
            <h4>Calendar Sync</h4>
            <div className="setting-row">
              <label>Google Calendar</label>
              <input
                type="checkbox"
                checked={settings.integrations?.googleCalendar?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'googleCalendar.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Outlook Calendar</label>
              <input
                type="checkbox"
                checked={settings.integrations?.outlookCalendar?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'outlookCalendar.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Apple Calendar</label>
              <input
                type="checkbox"
                checked={settings.integrations?.appleCalendar?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'appleCalendar.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Sync Frequency</label>
              <select 
                value={settings.integrations?.calendarSync?.frequency || 'realtime'}
                onChange={(e) => onSettingChange('integrations', 'calendarSync.frequency', e.target.value)}
              >
                <option value="realtime">Real-time</option>
                <option value="hourly">Every hour</option>
                <option value="daily">Daily</option>
                <option value="manual">Manual only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Communication Platforms</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Email Services</h4>
            <div className="setting-row">
              <label>Email Provider</label>
              <select 
                value={settings.integrations?.emailProvider || 'sendgrid'}
                onChange={(e) => onSettingChange('integrations', 'emailProvider', e.target.value)}
              >
                <option value="sendgrid">SendGrid</option>
                <option value="mailgun">Mailgun</option>
                <option value="ses">Amazon SES</option>
                <option value="smtp">Custom SMTP</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Email Tracking</label>
              <input
                type="checkbox"
                checked={settings.integrations?.email?.trackingEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'email.trackingEnabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Email Analytics</label>
              <input
                type="checkbox"
                checked={settings.integrations?.email?.analyticsEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'email.analyticsEnabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Bounce Handling</label>
              <input
                type="checkbox"
                checked={settings.integrations?.email?.bounceHandling || false}
                onChange={(e) => onSettingChange('integrations', 'email.bounceHandling', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>SMS Services</h4>
            <div className="setting-row">
              <label>SMS Provider</label>
              <select 
                value={settings.integrations?.smsProvider || 'twilio'}
                onChange={(e) => onSettingChange('integrations', 'smsProvider', e.target.value)}
              >
                <option value="twilio">Twilio</option>
                <option value="textmagic">TextMagic</option>
                <option value="clicksend">ClickSend</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="setting-row">
              <label>SMS Delivery Reports</label>
              <input
                type="checkbox"
                checked={settings.integrations?.sms?.deliveryReports || false}
                onChange={(e) => onSettingChange('integrations', 'sms.deliveryReports', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Two-way SMS</label>
              <input
                type="checkbox"
                checked={settings.integrations?.sms?.twoWayEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'sms.twoWayEnabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>SMS Shortlinks</label>
              <input
                type="checkbox"
                checked={settings.integrations?.sms?.shortlinksEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'sms.shortlinksEnabled', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Business Intelligence & Analytics</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Analytics Platforms</h4>
            <div className="setting-row">
              <label>Google Analytics</label>
              <input
                type="checkbox"
                checked={settings.integrations?.googleAnalytics?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'googleAnalytics.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Google Analytics 4</label>
              <input
                type="checkbox"
                checked={settings.integrations?.googleAnalytics4?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'googleAnalytics4.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Microsoft Power BI</label>
              <input
                type="checkbox"
                checked={settings.integrations?.powerbi?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'powerbi.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Tableau</label>
              <input
                type="checkbox"
                checked={settings.integrations?.tableau?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'tableau.enabled', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Reporting & Dashboards</h4>
            <div className="setting-row">
              <label>Automated Reports</label>
              <input
                type="checkbox"
                checked={settings.integrations?.reporting?.automated || false}
                onChange={(e) => onSettingChange('integrations', 'reporting.automated', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Report Frequency</label>
              <select 
                value={settings.integrations?.reporting?.frequency || 'weekly'}
                onChange={(e) => onSettingChange('integrations', 'reporting.frequency', e.target.value)}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Custom Dashboard</label>
              <input
                type="checkbox"
                checked={settings.integrations?.dashboard?.custom || false}
                onChange={(e) => onSettingChange('integrations', 'dashboard.custom', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Real-time Metrics</label>
              <input
                type="checkbox"
                checked={settings.integrations?.metrics?.realtime || false}
                onChange={(e) => onSettingChange('integrations', 'metrics.realtime', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>CRM & Customer Management</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>CRM Integration</h4>
            <div className="setting-row">
              <label>Salesforce</label>
              <input
                type="checkbox"
                checked={settings.integrations?.salesforce?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'salesforce.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>HubSpot</label>
              <input
                type="checkbox"
                checked={settings.integrations?.hubspot?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'hubspot.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Microsoft Dynamics</label>
              <input
                type="checkbox"
                checked={settings.integrations?.dynamics?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'dynamics.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Data Sync Direction</label>
              <select 
                value={settings.integrations?.crm?.syncDirection || 'bidirectional'}
                onChange={(e) => onSettingChange('integrations', 'crm.syncDirection', e.target.value)}
              >
                <option value="bidirectional">Two-way sync</option>
                <option value="from_crm">From CRM only</option>
                <option value="to_crm">To CRM only</option>
                <option value="manual">Manual sync only</option>
              </select>
            </div>
          </div>

          <div className="setting-card">
            <h4>Marketing Automation</h4>
            <div className="setting-row">
              <label>Mailchimp</label>
              <input
                type="checkbox"
                checked={settings.integrations?.mailchimp?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'mailchimp.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Constant Contact</label>
              <input
                type="checkbox"
                checked={settings.integrations?.constantcontact?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'constantcontact.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Campaign Monitor</label>
              <input
                type="checkbox"
                checked={settings.integrations?.campaignmonitor?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'campaignmonitor.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Auto-sync Customer Lists</label>
              <input
                type="checkbox"
                checked={settings.integrations?.marketing?.autoSync || false}
                onChange={(e) => onSettingChange('integrations', 'marketing.autoSync', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Healthcare-Specific Integrations</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>Electronic Health Records</h4>
            <div className="setting-row">
              <label>Epic MyChart</label>
              <input
                type="checkbox"
                checked={settings.integrations?.epic?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'epic.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Cerner</label>
              <input
                type="checkbox"
                checked={settings.integrations?.cerner?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'cerner.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>AllScripts</label>
              <input
                type="checkbox"
                checked={settings.integrations?.allscripts?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'allscripts.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>FHIR Compliance</label>
              <input
                type="checkbox"
                checked={settings.integrations?.fhir?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'fhir.enabled', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Insurance & Billing</h4>
            <div className="setting-row">
              <label>Insurance Verification</label>
              <input
                type="checkbox"
                checked={settings.integrations?.insurance?.verification || false}
                onChange={(e) => onSettingChange('integrations', 'insurance.verification', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Claims Processing</label>
              <input
                type="checkbox"
                checked={settings.integrations?.claims?.processing || false}
                onChange={(e) => onSettingChange('integrations', 'claims.processing', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Prior Authorization</label>
              <input
                type="checkbox"
                checked={settings.integrations?.authorization?.prior || false}
                onChange={(e) => onSettingChange('integrations', 'authorization.prior', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Electronic Prescribing</label>
              <input
                type="checkbox"
                checked={settings.integrations?.eprescribing?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'eprescribing.enabled', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>API & Webhook Settings</h3>
        <div className="settings-cards">
          <div className="setting-card">
            <h4>API Configuration</h4>
            <div className="setting-row">
              <label>REST API Enabled</label>
              <input
                type="checkbox"
                checked={settings.integrations?.api?.restEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'api.restEnabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>GraphQL API</label>
              <input
                type="checkbox"
                checked={settings.integrations?.api?.graphqlEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'api.graphqlEnabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>API Rate Limiting</label>
              <select 
                value={settings.integrations?.api?.rateLimit || 'standard'}
                onChange={(e) => onSettingChange('integrations', 'api.rateLimit', e.target.value)}
              >
                <option value="none">No limit</option>
                <option value="standard">Standard (1000/hour)</option>
                <option value="strict">Strict (100/hour)</option>
                <option value="enterprise">Enterprise (10000/hour)</option>
              </select>
            </div>
            <div className="setting-row">
              <label>API Documentation</label>
              <input
                type="checkbox"
                checked={settings.integrations?.api?.docsEnabled || false}
                onChange={(e) => onSettingChange('integrations', 'api.docsEnabled', e.target.checked)}
              />
            </div>
          </div>

          <div className="setting-card">
            <h4>Webhooks</h4>
            <div className="setting-row">
              <label>Webhooks Enabled</label>
              <input
                type="checkbox"
                checked={settings.integrations?.webhooks?.enabled || false}
                onChange={(e) => onSettingChange('integrations', 'webhooks.enabled', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Webhook Security</label>
              <select 
                value={settings.integrations?.webhooks?.security || 'hmac'}
                onChange={(e) => onSettingChange('integrations', 'webhooks.security', e.target.value)}
              >
                <option value="none">None</option>
                <option value="hmac">HMAC Signature</option>
                <option value="jwt">JWT Token</option>
                <option value="apikey">API Key</option>
              </select>
            </div>
            <div className="setting-row">
              <label>Retry Failed Webhooks</label>
              <input
                type="checkbox"
                checked={settings.integrations?.webhooks?.retry || false}
                onChange={(e) => onSettingChange('integrations', 'webhooks.retry', e.target.checked)}
              />
            </div>
            <div className="setting-row">
              <label>Webhook Logging</label>
              <input
                type="checkbox"
                checked={settings.integrations?.webhooks?.logging || false}
                onChange={(e) => onSettingChange('integrations', 'webhooks.logging', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="btn btn-secondary"
          onClick={() => onSlideoutOpen('integrationsManager')}
        >
          Manage Integrations
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => onSlideoutOpen('apiKeysManager')}
        >
          API Keys & Credentials
        </button>
      </div>
    </div>
  );
};

// Slideout editor components
const BusinessHoursEditor = ({ isOpen, onClose, data, onSave }) => {
  const [formData, setFormData] = useState(data || {});

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <div className="slideout-form">
      <div className="slideout-header">
        <h3>Edit Business Hours</h3>
        <button onClick={onClose} className="btn-close">×</button>
      </div>
      <div className="slideout-content">
        <div className="form-group">
          <label>Monday</label>
          <div className="time-row">
            <input
              type="time"
              value={formData.monday?.open || '09:00'}
              onChange={(e) => setFormData({
                ...formData,
                monday: { ...formData.monday, open: e.target.value }
              })}
            />
            <span>to</span>
            <input
              type="time"
              value={formData.monday?.close || '17:00'}
              onChange={(e) => setFormData({
                ...formData,
                monday: { ...formData.monday, close: e.target.value }
              })}
            />
          </div>
        </div>
        <div className="slideout-actions">
          <button onClick={handleSave} className="btn btn-primary">Save Changes</button>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const EmailTemplateEditor = ({ isOpen, onClose, data, onSave }) => {
  const [formData, setFormData] = useState(data || { subject: '', body: '' });

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <div className="slideout-form">
      <div className="slideout-header">
        <h3>Edit Email Template</h3>
        <button onClick={onClose} className="btn-close">×</button>
      </div>
      <div className="slideout-content">
        <div className="form-group">
          <label>Subject Line</label>
          <input
            type="text"
            value={formData.subject || ''}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Email subject..."
          />
        </div>
        <div className="form-group">
          <label>Email Body</label>
          <textarea
            value={formData.body || ''}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            placeholder="Email content..."
            rows="8"
          />
        </div>
        <div className="template-variables">
          <p><strong>Available Variables:</strong></p>
          <div className="variable-tags">
            <span className="variable-tag">{'{'}{'{'}}customer_name{'}'}{'}'}</span>
            <span className="variable-tag">{'{'}{'{'}}appointment_date{'}'}{'}'}</span>
            <span className="variable-tag">{'{'}{'{'}}appointment_time{'}'}{'}'}</span>
          </div>
        </div>
        <div className="slideout-actions">
          <button onClick={handleSave} className="btn btn-primary">Save Template</button>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const AppointmentPoliciesEditor = ({ isOpen, onClose, data, onSave }) => {
  const [formData, setFormData] = useState(data || {});

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <div className="slideout-form">
      <div className="slideout-header">
        <h3>Edit Appointment Policies</h3>
        <button onClick={onClose} className="btn-close">×</button>
      </div>
      <div className="slideout-content">
        <div className="form-group">
          <label>Cancellation Window (hours)</label>
          <input
            type="number"
            value={formData.cancellationWindow || 24}
            onChange={(e) => setFormData({ ...formData, cancellationWindow: parseInt(e.target.value) })}
            min="1"
          />
        </div>
        <div className="form-group">
          <label>Cancellation Fee (£)</label>
          <input
            type="number"
            value={formData.cancellationFee || 0}
            onChange={(e) => setFormData({ ...formData, cancellationFee: parseFloat(e.target.value) })}
            min="0"
            step="0.01"
          />
        </div>
        <div className="form-group">
          <label>No-show Fee (£)</label>
          <input
            type="number"
            value={formData.noShowFee || 0}
            onChange={(e) => setFormData({ ...formData, noShowFee: parseFloat(e.target.value) })}
            min="0"
            step="0.01"
          />
        </div>
        <div className="slideout-actions">
          <button onClick={handleSave} className="btn btn-primary">Save Policies</button>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const SecuritySettingsEditor = ({ isOpen, onClose, data, onSave }) => {
  const [formData, setFormData] = useState(data || {});

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <div className="slideout-form">
      <div className="slideout-header">
        <h3>Security Settings</h3>
        <button onClick={onClose} className="btn-close">×</button>
      </div>
      <div className="slideout-content">
        <div className="form-group">
          <label>Session Timeout (minutes)</label>
          <select
            value={formData.sessionTimeout || 30}
            onChange={(e) => setFormData({ ...formData, sessionTimeout: parseInt(e.target.value) })}
          >
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">60 minutes</option>
            <option value="120">2 hours</option>
          </select>
        </div>
        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.twoFactorAuth || false}
                onChange={(e) => setFormData({ ...formData, twoFactorAuth: e.target.checked })}
              />
              Enable Two-Factor Authentication
            </label>
          </div>
        </div>
        <div className="slideout-actions">
          <button onClick={handleSave} className="btn btn-primary">Save Settings</button>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;